import React from 'react';
import PropTypes from 'prop-types';
import { H2 } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Section, Container, Row, Column } from 'components/core/Page';
import { MarkdownWrapper } from 'components/atoms';
import './ContentSection.scss';

// `description` is intentionally supported only in the non-image (column) layout.
// The image layout uses a side-by-side format where the description would disrupt the visual flow.
const ContentSection = ({
	title,
	description,
	content,
	contentType,
	theme = 'white',
	image,
	pageType,
	titleTag = 'h2',
}) => {
	const color = theme === 'dark' ? 'white' : '';
	const sgpClass = pageType?.toLowerCase() === 'sgp' ? 'sgp' : '';
	const TitleTag = titleTag === 'h3' ? 'h3' : H2;

	if (image) {
		return (
			<Section
				className={`content-section ${theme}${sgpClass ? ` ${sgpClass}` : ''} image-text-layout`}
			>
				<Container>
					<div className="image-text-container">
						<div className="image-text-left">
							<Image
								src={image}
								alt={title}
								className="content-image"
							/>
						</div>
						<div className="image-text-right">
							<TitleTag
								className="title"
								color={color}
							>
								{title}
							</TitleTag>
							{content.map((item, index) => (
								<MarkdownWrapper
									key={index}
									content={[{ value: item }]}
									componentProps={{
										color,
										className: item?.includes('https://')
											? 'bright-link'
											: '',
									}}
								/>
							))}
						</div>
					</div>
				</Container>
			</Section>
		);
	}

	return (
		<Section
			className={`content-section ${theme}${sgpClass ? ` ${sgpClass}` : ''}`}
		>
			<Container>
				<Row>
					<Column medium={12}>
						<div className="content-section-container">
							<div className="content-header">
								<TitleTag
									className="title"
									color={color}
								>
									{title}
								</TitleTag>

								{description && (
									<MarkdownWrapper
										content={[{ value: description }]}
										componentProps={{
											color,
											className: description.includes(
												'https://',
											)
												? 'bright-link'
												: '',
										}}
									/>
								)}
							</div>

							<Row className={`content ${contentType}`}>
								{content.map((item) => (
									<Column
										key={item}
										medium={12}
										large={
											contentType === 'row-type' ? 12 : 4
										}
										className={`content-column ${color}`}
									>
										<MarkdownWrapper
											content={[{ value: item }]}
											componentProps={{
												color,
												className: item?.includes(
													'https://',
												)
													? 'bright-link'
													: '',
											}}
										/>
									</Column>
								))}
							</Row>
						</div>
					</Column>
				</Row>
			</Container>
		</Section>
	);
};

ContentSection.propTypes = {
	title: PropTypes.string,
	description: PropTypes.string,
	content: PropTypes.arrayOf(PropTypes.string),
	contentType: PropTypes.string,
	theme: PropTypes.oneOf(['white', 'light-Blue', 'dark', 'light']),
	image: PropTypes.string,
	pageType: PropTypes.string,
	titleTag: PropTypes.oneOf(['h2', 'h3']),
};

export default ContentSection;
